import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { animate } from "framer-motion";
import { useInViewAfterMount } from "../hooks/useInViewAfterMount";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const magFacts = [
  { id: "people", target: 30, suffix: "+", label: "People" },
  { id: "teams", target: 3, suffix: "", label: "Teams" },
];

const executionFact = {
  id: "execution",
  target: 100,
  suffix: "%",
  label: "In-house execution",
};

const panels = {
  people: [
    { label: "Headcount", value: "30+" },
    { label: "Founded", value: "2024" },
    { label: "Region", value: "Metro Detroit" },
    { label: "Deployment", value: "Global" },
  ],
  teams: [
    { label: "01", value: "Solutions Engineering" },
    { label: "02", value: "Technology & Integration" },
    { label: "03", value: "Maintenance & Support" },
  ],
};

const DONUT_R = 34;
const DONUT_C = 213.63;

function TeamDonut({ mode, compact }) {
  const gap = 12;
  const segment = ((DONUT_C - gap * 3) / 3).toFixed(2);
  const remainder = (DONUT_C - Number(segment)).toFixed(2);
  const people = mode === "people";

  return (
    <div className="flex items-center justify-center px-4 py-5" aria-hidden="true">
      <svg
        viewBox="0 0 100 100"
        className={compact ? "h-36 w-36" : "h-44 w-44 sm:h-52 sm:w-52"}
      >
        <circle cx="50" cy="50" r={DONUT_R} fill="none" stroke="#e5e7eb" strokeWidth="7" />
        {people ? (
          <circle
            cx="50"
            cy="50"
            r={DONUT_R}
            fill="none"
            stroke="#10b981"
            strokeWidth="7"
            strokeDasharray="213.63"
            transform="rotate(-90 50 50)"
          />
        ) : (
          [0, 120, 240].map((deg) => (
            <circle
              key={deg}
              cx="50"
              cy="50"
              r={DONUT_R}
              fill="none"
              stroke="#10b981"
              strokeWidth="7"
              strokeDasharray={`${segment} ${remainder}`}
              transform={`rotate(${-90 + deg} 50 50)`}
            />
          ))
        )}
        <text
          x="50"
          y="54"
          textAnchor="middle"
          fill="#111827"
          fontSize="18"
          fontWeight="700"
        >
          {people ? "30+" : "3"}
        </text>
        <text
          x="50"
          y="64"
          textAnchor="middle"
          fill="#6b7280"
          fontSize="5.5"
          fontWeight="600"
          letterSpacing="0.16em"
        >
          {people ? "PEOPLE" : "TEAMS"}
        </text>
      </svg>
    </div>
  );
}

function CountValue({ fact, play, instant }) {
  const [value, setValue] = useState(fact.target);

  useEffect(() => {
    if (instant || !play) {
      setValue(fact.target);
      return undefined;
    }
    const controls = animate(0, fact.target, {
      duration: 1.05,
      ease: [0.12, 0.82, 0.2, 1],
      onUpdate: (next) => setValue(Math.round(next)),
    });
    return () => controls.stop();
  }, [fact.target, instant, play]);

  return (
    <span className="tabular-nums">{`${value}${fact.suffix}`}</span>
  );
}

export default function MagTeamBoard({ compact = false }) {
  const boardRef = useRef(null);
  const reduceMotion = usePrefersReducedMotion();
  const seen = useInViewAfterMount(boardRef, { amount: compact ? 0.12 : 0.28, once: true });
  const play = !reduceMotion && seen;
  const instant = !play;
  const [mode, setMode] = useState("people");
  const rows = panels[mode];

  return (
    <section
      ref={boardRef}
      className={compact ? "" : "bg-white px-4 py-12 sm:px-6 sm:py-14 lg:px-8"}
      aria-labelledby="mag-team-heading"
    >
        <h2
        id="mag-team-heading"
        className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl"
      >Meet the MAG Team</h2>

      <div className="mt-6 border border-gray-200 bg-white">
        <div
          className="flex flex-col divide-y divide-gray-200 border-b border-gray-200"
          role="tablist"
          aria-label="MAG numbers"
        >
          {magFacts.map((fact) => {
            const on = mode === fact.id;
            return (
              <button
                key={fact.id}
                type="button"
                role="tab"
                aria-selected={on}
                aria-controls="mag-org-panel"
                onClick={() => setMode(fact.id)}
                className={
                  on
                    ? "flex flex-col items-start bg-gray-900 px-4 py-4 text-left text-white sm:px-5"
                    : "flex flex-col items-start bg-white px-4 py-4 text-left text-gray-900 hover:bg-gray-50 sm:px-5"
                }
              >
                <p className="text-3xl font-bold tracking-tight sm:text-4xl">
                  <CountValue fact={fact} play={play} instant={instant} />
                </p>
                <p className="mt-1 text-sm font-semibold">{fact.label}</p>
              </button>
            );
          })}
        </div>

        <div id="mag-org-panel" role="tabpanel">
          <dl>
            {rows.map((row) => (
              <div
                key={row.label}
                className="grid grid-cols-[7.5rem_1fr] items-center gap-3 border-b border-gray-200 px-4 py-3 last:border-b-0 sm:grid-cols-[9rem_1fr] sm:px-5"
              >
                <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                  {row.label}
                </dt>
                <dd className="text-sm font-semibold text-gray-900">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="border-t border-gray-200">
          <TeamDonut mode={mode} compact={compact} />
        </div>

        <div className="flex items-baseline gap-4 border-t border-gray-200 px-4 py-3 sm:px-5">
          <p className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            <CountValue fact={executionFact} play={play} instant={instant} />
          </p>
          <p className="text-sm font-semibold text-gray-900">{executionFact.label}</p>
        </div>
      </div>

      <Link to="/careers" className="btn-primary-dark mt-6" discover="none">
        Join Our Team
      </Link>
    </section>
  );
}
